import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import { Ticket, TicketStatus } from 'src/interfaces/TicketTypes';
import StatusChip from './StatusChip';
import CreateTicketModal from './CreateTicketModal';

interface Props {
  installationId: number;
}

const statusCountKeys: {
  status: number;
  id: string;
  defaultMessage: string;
  color: string;
}[] = [
  { status: TicketStatus.Open, id: 'statusOpen', defaultMessage: 'Open', color: '#d32f2f' },
  { status: TicketStatus.InProgress, id: 'statusInProgress', defaultMessage: 'In Progress', color: '#ed6c02' },
  { status: TicketStatus.Escalated, id: 'statusEscalated', defaultMessage: 'Escalated', color: '#9c27b0' },
  { status: TicketStatus.Resolved, id: 'statusResolved', defaultMessage: 'Resolved', color: '#2e7d32' },
  { status: TicketStatus.Closed, id: 'statusClosed', defaultMessage: 'Closed', color: '#757575' }
];

function InstallationTicketsTab({ installationId }: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const fetchTickets = () => {
    axiosConfig
      .get('/GetTicketsForInstallation', { params: { installationId } })
      .then((res) => {
        setTickets(res.data);
        setError('');
      })
      .catch(() => setError('Failed to load tickets.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTickets();
  }, [installationId]);

  const statusCounts = statusCountKeys.map((s) => ({
    ...s,
    count: tickets.filter((t) => t.status === s.status).length
  }));

  if (loading) return null;

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          <FormattedMessage id="tickets" defaultMessage="Tickets" />
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          <FormattedMessage id="createTicket" defaultMessage="Create Ticket" />
        </Button>
      </Box>

      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
        {statusCounts.map((s) => (
          <Chip
            key={s.status}
            label={`${intl.formatMessage({ id: s.id, defaultMessage: s.defaultMessage })}: ${s.count}`}
            size="small"
            sx={{
              backgroundColor: s.count > 0 ? s.color : '#e0e0e0',
              color: s.count > 0 ? '#fff' : '#757575',
              fontWeight: 600
            }}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tickets.length === 0 && !error ? (
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage id="noTickets" defaultMessage="No tickets found." />
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>
                  <FormattedMessage id="subject" defaultMessage="Subject" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="status" defaultMessage="Status" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="priority" defaultMessage="Priority" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="createdAt" defaultMessage="Created" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets
                .sort(
                  (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                )
                .map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <TableCell>{ticket.id}</TableCell>
                    <TableCell>{ticket.subject}</TableCell>
                    <TableCell>
                      <StatusChip status={ticket.status} />
                    </TableCell>
                    <TableCell>
                      {intl.formatMessage({
                        id: `priority${['Critical', 'High', 'Medium', 'Low'][ticket.priority]}`,
                        defaultMessage: ['Critical', 'High', 'Medium', 'Low'][
                          ticket.priority
                        ]
                      })}
                    </TableCell>
                    <TableCell>
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CreateTicketModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          fetchTickets();
        }}
        defaultInstallationId={installationId}
      />
    </Box>
  );
}

export default InstallationTicketsTab;
